import { createPinia } from 'pinia';
import {ref, computed} from "vue";

/** Shared pinia instance for all Vue apps on the page */
export const pinia = createPinia();

export type LoadState = "init" | "loading" | "loaded" | "error";


/**
 * Helper function for lazy-loading any data
 * @param fetchFunction	function to directly contact backend for data
 * @param placeholder value which will be returned when data aren't loaded yet
 * @param maxAge expiration of the data in ms
 */
export function lazyLoad<T>(fetchFunction: () => Promise<T>, placeholder:T = null, maxAge: number = Infinity) {
	const data = ref<T>(placeholder);
	const fetchState = ref<LoadState>("init");
	const lastFetchTime = ref<number>(0);

	async function loadData() {
		try {
			fetchState.value = "loading";
			data.value = await fetchFunction();
			lastFetchTime.value = Date.now();
			fetchState.value = "loaded";
		} catch (e) {
			fetchState.value = "error";
			console.error(e);
		}
	}

	function expired():boolean {
		return (Date.now() - lastFetchTime.value) > maxAge;
	}

	// Computed property to trigger fetch only when accessed
	const computedData = computed<T>(() => {
		const state = fetchState.value;
		if (state === "init" || state === "error" || (state === "loaded" && expired())) loadData();
		return data.value ?? placeholder;
	});

	// Function to manually invalidate the cache
	function dirty() {
		fetchState.value = "init";
	}

	return { data: computedData, dirty };
}
